/// Inline component for displaying AI-generated diagrams in the chat panel.
///
/// Shows a success indicator with action buttons to open the generated diagram
/// in a new editor tab or view the raw XML/JSON source. Supports both BPMN XML
/// and N8N JSON node flow formats.

import { useState, useCallback } from "react";
import { FileCode, Eye, EyeOff, CheckCircle } from "lucide-react";
import { useEditorStore } from "@/stores/editorStore";
import { useAIStore } from "@/stores/aiStore";
import type { DiagramType } from "@/types/ai";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AIGeneratedDiagramProps {
  content: string;
  type: DiagramType;
  rawResponse: string;
}

// ---------------------------------------------------------------------------
// Content extraction utility
// ---------------------------------------------------------------------------

/**
 * Extract diagram content (XML or JSON) from an LLM response string.
 *
 * Tries fenced code blocks first (```xml or ```json), then falls back to
 * heuristic detection of raw XML (`<?xml` or `<bpmn:`) or JSON (`{`).
 *
 * @returns The extracted content string, or null if nothing found.
 */
export function extractDiagramContent(rawResponse: string, type: DiagramType): string | null {
  if (type === "bpmn") {
    // Try fenced ```xml block
    const xmlFenceMatch = rawResponse.match(/```xml\s*\n([\s\S]*?)```/);
    if (xmlFenceMatch) return xmlFenceMatch[1].trim();

    // Fallback: find raw XML starting with <?xml or <bpmn:
    const xmlStartMatch = rawResponse.match(
      /((<\?xml[\s\S]*?<\/bpmn:definitions>)|(<bpmn:definitions[\s\S]*?<\/bpmn:definitions>))/,
    );
    if (xmlStartMatch) return xmlStartMatch[0].trim();

    return null;
  }

  // type === "nodeflow"
  // Try fenced ```json block
  const jsonFenceMatch = rawResponse.match(/```json\s*\n([\s\S]*?)```/);
  if (jsonFenceMatch) return jsonFenceMatch[1].trim();

  // Fallback: find raw JSON starting with {
  const jsonStartIdx = rawResponse.indexOf("{");
  if (jsonStartIdx >= 0) {
    // Try to find matching closing brace
    let depth = 0;
    for (let i = jsonStartIdx; i < rawResponse.length; i++) {
      if (rawResponse[i] === "{") depth++;
      else if (rawResponse[i] === "}") depth--;
      if (depth === 0) {
        return rawResponse.slice(jsonStartIdx, i + 1).trim();
      }
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AIGeneratedDiagram({ content, type, rawResponse }: AIGeneratedDiagramProps) {
  const [showSource, setShowSource] = useState(false);

  const handleOpenInEditor = useCallback(() => {
    const editorStore = useEditorStore.getState();
    const aiStore = useAIStore.getState();

    // Check if this content is already open in an existing AI-generated tab.
    // If so, switch to that tab instead of creating a duplicate.
    const existingTab = editorStore.tabs.find(
      (t) => t.editorType === type && t.content === content && t.id.startsWith("ai-"),
    );
    if (existingTab) {
      editorStore.setActiveTab(existingTab.id);
      return;
    }

    const tabId = `ai-${type}-${Date.now()}`;
    const title = type === "bpmn" ? "[AI] BPMN Process" : "[AI] Node Flow";

    // Track generation result for iterative refinement
    aiStore.setGenerationResult(tabId, { type, content, rawResponse });

    // Create a new editor tab with the generated content.
    // The EditorCanvas auto-load effect will load the content into the WASM
    // worker once it becomes ready, so no flowy:loadDocument dispatch is needed.
    editorStore.addTab({
      id: tabId,
      title,
      editorType: type,
      content,
      dirty: false,
      canUndo: false,
      canRedo: false,
    });
  }, [content, type, rawResponse]);

  const handleToggleSource = useCallback(() => {
    setShowSource((prev) => !prev);
  }, []);

  const lang = type === "bpmn" ? "XML" : "JSON";

  return (
    <div className="my-2 rounded-lg border border-zinc-700 bg-zinc-800/70 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-700/50">
        <CheckCircle size={16} className="text-green-400 flex-shrink-0" />
        <span className="text-sm text-zinc-200 font-medium">
          {type === "bpmn" ? "BPMN diagram" : "Node flow"} generated
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={handleOpenInEditor}
          className="flex items-center gap-1.5 rounded-md bg-blue-600 hover:bg-blue-500 px-3 py-1.5 text-xs text-white font-medium transition-colors"
        >
          <FileCode size={14} />
          Open in Editor
        </button>
        <button
          type="button"
          onClick={handleToggleSource}
          className="flex items-center gap-1.5 rounded-md border border-zinc-600 hover:border-zinc-500 px-3 py-1.5 text-xs text-zinc-300 hover:text-zinc-200 transition-colors"
        >
          {showSource ? <EyeOff size={14} /> : <Eye size={14} />}
          {showSource ? "Hide Source" : `View ${lang}`}
        </button>
      </div>

      {/* Collapsible source view */}
      {showSource && (
        <div className="border-t border-zinc-700/50 px-3 py-2 max-h-64 overflow-auto">
          <pre className="text-xs text-zinc-400 font-mono whitespace-pre-wrap break-words">
            {content}
          </pre>
        </div>
      )}
    </div>
  );
}
